/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

import PropTypes from "prop-types";
import {
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MDTypography from "components/MDTypography";
import { FilterBar, FilterSearch, FilterSelect } from "components/v2/filters";
import PearlLogoLoader from "components/PearlLogoLoader";
import colors from "styles/tokens/colors";
import { formatEnumLabel } from "./formatters";

const tokenColor = (path, fallback) => {
  try {
    return path.split(".").reduce((acc, k) => acc[k], colors) || fallback;
  } catch {
    return fallback;
  }
};

/**
 * A table for server-paginated lists ({items,total,page,page_size}). Columns
 * use the react-table shape the Travel configs already use (Header, accessor,
 * Cell({value,row:{original}}), align, width), so one column definition works
 * in either table. Search, filters, sort and paging are controlled by the
 * caller, which turns them into query parameters.
 */
const ServerDataTable = ({
  columns,
  rows,
  total,
  page,
  pageSize,
  loading,
  search,
  onSearchChange,
  searchPlaceholder,
  filters,
  filterValues,
  onFilterChange,
  sortBy,
  sortOrder,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onRowClick,
}) => {
  const border = tokenColor("chip.outlineBorder", "#C3CAD9");
  // UI v2: the v2 filter row and theme-v2's table density (48px rows, 12px
  // uppercase header). Same values and callbacks; legacy markup unchanged.
  const isV2 = useTheme()?.pearlUi === "v2";
  const optionsOf = (filter) => [
    { value: "", label: "All" },
    ...(filter.options || []).map((o) => (typeof o === "object" ? o : { value: o, label: formatEnumLabel(o) })),
  ];
  const toolbar = isV2 ? (
    <FilterBar sx={{ px: 2, pb: 2 }}>
      {onSearchChange ? (
        <FilterSearch value={search} onChange={(v) => onSearchChange(v)} placeholder={searchPlaceholder} />
      ) : null}
      {(filters || []).map((filter) => (
        <FilterSelect
          key={filter.param}
          label={filter.label}
          value={filterValues?.[filter.param] ?? ""}
          onChange={(v) => onFilterChange(filter.param, v)}
          options={optionsOf(filter)}
        />
      ))}
    </FilterBar>
  ) : null;
  return (
    <Box>
      {toolbar || (
      <Box display="flex" flexWrap="wrap" gap={2} px={2} pb={2} alignItems="center">
        {onSearchChange ? (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ minWidth: 240 }}
          />
        ) : null}
        {(filters || []).map((filter) => (
          <TextField
            key={filter.param}
            select
            size="small"
            label={filter.label}
            value={filterValues?.[filter.param] ?? ""}
            onChange={(e) => onFilterChange(filter.param, e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {(filter.options || []).map((o) => {
              const opt = typeof o === "object" ? o : { value: o, label: formatEnumLabel(o) };
              return (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              );
            })}
          </TextField>
        ))}
      </Box>
      )}
      <TableContainer sx={{ boxShadow: "none" }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              {columns.map((col) => {
                const sortKey = col.sortKey || col.accessor;
                const sortable = onSortChange && !col.disableSortBy && col.accessor !== "actions";
                return (
                  <TableCell
                    key={col.accessor}
                    align={col.align || "left"}
                    sx={isV2 ? { width: col.width, whiteSpace: "nowrap" } : {
                      width: col.width,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color: "text.secondary",
                      borderBottom: `1px solid ${border}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={sortBy === sortKey}
                        direction={sortBy === sortKey ? sortOrder : "asc"}
                        onClick={() =>
                          onSortChange(
                            sortKey,
                            sortBy === sortKey && sortOrder === "asc" ? "desc" : "asc"
                          )
                        }
                      >
                        {col.Header}
                      </TableSortLabel>
                    ) : (
                      col.Header
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box py={6} display="flex" justifyContent="center">
                    <PearlLogoLoader size="md" message="Loading data..." />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <MDTypography variant="button" color="text" display="block" textAlign="center" py={4}>
                    No records found.
                  </MDTypography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={row.id ?? index}
                  hover
                  sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  onClick={(e) => {
                    // Clicks on buttons/menus inside a cell are theirs.
                    if (e.target.closest("button, a, [role='button'], [role='menuitem']")) return;
                    onRowClick?.(row);
                  }}
                >
                  {columns.map((col) => {
                    const value = row?.[col.accessor];
                    const Cell = col.Cell;
                    return (
                      <TableCell
                        key={col.accessor}
                        align={col.align || "left"}
                        sx={isV2 ? undefined : { fontSize: "0.8rem", borderBottom: `1px solid ${border}` }}
                      >
                        {Cell ? (
                          <Cell value={value} row={{ original: row }} />
                        ) : value === null || value === undefined || value === "" ? (
                          "-"
                        ) : (
                          String(value)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={Math.max(0, page - 1)}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, p) => onPageChange(p + 1)}
        onRowsPerPageChange={(e) => onPageSizeChange(Number(e.target.value))}
      />
    </Box>
  );
};

ServerDataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  search: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  filters: PropTypes.arrayOf(PropTypes.object),
  filterValues: PropTypes.object,
  onFilterChange: PropTypes.func,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.oneOf(["asc", "desc"]),
  onSortChange: PropTypes.func,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
};

ServerDataTable.defaultProps = {
  loading: false,
  search: "",
  onSearchChange: null,
  searchPlaceholder: "Search...",
  filters: [],
  filterValues: {},
  onFilterChange: () => {},
  sortBy: null,
  sortOrder: "asc",
  onSortChange: null,
  onRowClick: null,
};

export default ServerDataTable;
